import { getAuth } from 'firebase/auth';
import { doc, collection, addDoc, updateDoc, getDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { API_ENDPOINTS } from '../config/apiConfig';
import React from 'react';
import { db } from '../firebaseConfig'; // Fixed import path

// Define whitelist emails as a constant
const WHITELISTED_EMAILS = [
    "beautgroup@gmail.com", 
    "hari765@gmail.com", 
    "lakshman52264@gmail.com",
    "gptonly522@gmail.com"
];

// This is a utility function to check if a user is whitelisted
export const checkIfUserIsWhitelisted = (email) => {
    if (!email) return false;
    
    // Debug logging
    console.log(`Checking if ${email} is in whitelist:`, WHITELISTED_EMAILS);
    
    // Exact match check (case insensitive)
    const normalizedEmail = email.toLowerCase().trim();
    const isWhitelisted = WHITELISTED_EMAILS.some(
        whitelistedEmail => whitelistedEmail.toLowerCase().trim() === normalizedEmail
    );
    
    console.log(`Email ${email} is ${isWhitelisted ? '' : 'NOT '}whitelisted`);
    return isWhitelisted;
};

// Function to check payment status
export const checkPaymentStatus = async (currentUser, db) => {
    if (!currentUser || !currentUser.uid) return false;
    
    try {
        // Reference the payments collection
        const paymentsCollectionRef = collection(db, `customers/${currentUser.uid}/payments`);
        const querySnapshot = await getDocs(paymentsCollectionRef);

        if (!querySnapshot.empty) {
            // Assuming there's only one document in the collection
            const paymentDoc = querySnapshot.docs[0]; // Get the first document
            const paymentData = paymentDoc.data();
            return paymentData.status === "succeeded"; // Return true if the payment succeeded
        } else {
            console.error("No payment documents found.");
            return false;
        }
    } catch (error) {
        console.error("Error checking payment status:", error);
        return false;
    }
};

// Function to get pricing for grid size
export const getPriceForGridSize = (size, userCountry) => {
    // Get the column count from the grid size (format: rowsxcolumns)
    const columnCount = size ? parseInt(size.split('x')[1]) : 0;
    
    // Updated pricing logic based on column count
    const getUSDPrice = (cols) => {
        if (cols <= 4) return 4;       // 4 columns = $4
        else if (cols === 5) return 5;  // 5 columns = $5
        else if (cols === 6) return 6;  // 6 columns = $6
        else if (cols === 7) return 7;  // 7 columns = $7
        else if (cols === 8) return 8;  // 8 columns = $8
        else if (cols === 9) return 9;  // 9 columns = $9
        else if (cols === 10) return 10;  
        else if (cols === 11) return 11;  
        else if (cols === 12) return 12;  
        else return 13;                 // 10+ columns = $10
    };

    // Get INR price based on specific mapping
    const getINRPrice = (cols) => {
        const priceMapping = {
            4: 334,
            5: 428,
            6: 514,
            7: 599,
            8: 685,
            9: 771,
            10: 856,
            11: 944,
            12: 1029,
            13: 1115
        };
        
        // For columns <= 4, use the $4 price mapping
        const key = cols <= 4 ? 4 : (cols >= 13 ? 13 : cols);
        return priceMapping[key] || 1115; // Default to highest price if not found
    };

    // Get price based on column count
    const priceInUSD = getUSDPrice(columnCount);
    const priceInRupees = getINRPrice(columnCount);

    // Return appropriate price based on user's country
    if (userCountry === 'IN') {
        return { priceInRupees, priceFormatted: `₹${priceInRupees.toLocaleString('en-IN')}` };
    }

    return { priceInRupees: null, priceFormatted: `$${priceInUSD}` };
};

// Enhanced handleAIClick function to return suggestions data directly
export const handleAIClick = async ({
    currentUser,
    db,
    sessionId,
    gridSize,
    setLoadingAI,
    setAIError,
    navigate
}) => {
    setLoadingAI(true);
    setAIError(null);
    
    try {
        console.log("Current user email:", currentUser?.email);
        
        // Check if user is whitelisted
        const isUserWhitelisted = checkIfUserIsWhitelisted(currentUser?.email);
        console.log("Is user whitelisted:", isUserWhitelisted);
        
        // If user is not whitelisted, check if they've made a payment
        if (!isUserWhitelisted) {
            console.log("User is not whitelisted, checking payment status");
            
            const hasSuccessfulPayment = await checkPaymentStatus(currentUser, db);
            console.log("Payment succeeded:", hasSuccessfulPayment);
            
            if (!hasSuccessfulPayment) {
                setLoadingAI(false);
                // Return result indicating payment is needed
                return { 
                    success: false,
                    showPaymentModal: true,
                    message: "Payment required for AI layouts"
                };
            }
        }
        
        // At this point, the user is either whitelisted or has made a payment
        // Send API request to generate layout suggestions
        try {
            console.log("Sending API request to:", API_ENDPOINTS.AI_SUGGESTIONS);
            console.log("Request payload:", { session_id: sessionId });
            
            const response = await fetch(API_ENDPOINTS.AI_SUGGESTIONS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ session_id: sessionId }),
            });

            console.log("API response status:", response.status);
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error("API error response:", errorText);
                throw new Error(`HTTP Error: ${response.status} ${errorText}`);
            }

            // Get the raw response text first for debugging
            const responseText = await response.text();
            console.log("Raw API response:", responseText);
            
            // More robust JSON parsing to handle nested JSON structures
            let analysisData;
            try {
                // First attempt: Parse the outer JSON
                const parsedResponse = JSON.parse(responseText);
                console.log("Parsed analysis data (outer layer):", parsedResponse);
                
                // If the response contains a string in 'suggestions' field, try to extract embedded JSON
                if (parsedResponse && typeof parsedResponse.suggestions === 'string') {
                    // Use regex to find JSON content within the string
                    const jsonPattern = /```(?:json)?\s*([\s\S]*?)```|(\{[\s\S]*\})/;
                    const match = jsonPattern.exec(parsedResponse.suggestions);
                    
                    if (match) {
                        const jsonContent = (match[1] || match[2]).trim();
                        console.log("Extracted JSON content:", jsonContent);
                        
                        try {
                            const extractedData = JSON.parse(jsonContent);
                            console.log("Successfully parsed extracted JSON:", extractedData);
                            
                            // Replace the string with the parsed object
                            analysisData = extractedData;
                        } catch (extractError) {
                            console.error("Failed to parse extracted JSON:", extractError);
                            // Fall back to the original response
                            analysisData = parsedResponse;
                        }
                    } else {
                        console.warn("No JSON pattern found in suggestions string");
                        // Use the original response when pattern not found
                        analysisData = parsedResponse;
                    }
                } else {
                    // If no embedded JSON, use the parsed response directly
                    analysisData = parsedResponse;
                }
            } catch (parseError) {
                console.error("JSON parse error:", parseError);
                console.error("Problematic JSON:", responseText);
                throw new Error(`Failed to parse API response: ${parseError.message}`);
            }
            
            // Final structure check before proceeding
            // Ensure we have the layout_suggestions array
            if (!analysisData) {
                throw new Error("API response missing data");
            }
            
            // If we have parsed data but no layout_suggestions, try to find them
            if (!analysisData.layout_suggestions) {
                // Check if suggestions field contains our layout suggestions
                if (analysisData.suggestions && typeof analysisData.suggestions === 'string') {
                    // Try to extract layout suggestions from the suggestions string
                    const layoutPattern = /"layout_suggestions"\s*:\s*(\[[\s\S]*?\])/;
                    const layoutMatch = layoutPattern.exec(analysisData.suggestions);
                    
                    if (layoutMatch) {
                        try {
                            // Extract and parse the layout suggestions array
                            const layoutsJson = layoutMatch[1];
                            const layoutsArray = JSON.parse(layoutsJson);
                            
                            // Add the layout_suggestions to our data object
                            analysisData.layout_suggestions = layoutsArray;
                            console.log("Extracted layout_suggestions from string:", layoutsArray);
                        } catch (layoutError) {
                            console.error("Failed to extract layout suggestions:", layoutError);
                        }
                    }
                }
            }
            
            // After all our attempts, if we still don't have layout_suggestions, create a fallback
            if (!analysisData.layout_suggestions) {
                console.error("No layout_suggestions found in response");
                // Remove the hardcoded fallback, allowing the calling code to handle this case
                analysisData.layout_suggestions = [];
            }
            
            // Save to session with all relevant fields 
            if (currentUser && currentUser.uid) {
                const sessionRef = doc(db, 'sessions', currentUser.uid);
                
                // Save both fields for compatibility
                const updateData = {
                    tileAnalysis: analysisData.tile_analysis || {},
                    layoutSuggestions: analysisData.layout_suggestions || [],
                    lastAIRequestTime: new Date().toISOString()
                };
                
                console.log("Saving analysis data to Firestore:", updateData);
                await updateDoc(sessionRef, updateData);
                console.log("Successfully saved analysis data to Firestore");
            }
            
            // Return the suggestions directly instead of navigating away
            return { 
                showPaymentModal: false, 
                success: true, 
                suggestions: analysisData
            };
        } catch (error) {
            console.error('Failed to fetch AI suggestions:', error);

            // Provide more user-friendly error message
            if (
                error.message &&
                (
                    error.message.includes('overloaded') ||
                    error.message.includes('Anthropic') ||
                    error.message.includes('Claude') ||
                    error.message.includes('429') ||
                    error.message.includes('timeout') ||
                    error.message.includes('rate limit') ||
                    error.message.includes('temporarily unavailable')
                )
            ) {
                setAIError("Our AI service is currently busy or unavailable. Please try again after some time.");
            } else {
                setAIError(`Failed to generate AI layouts: ${error.message}`);
            }

            return { showPaymentModal: false, success: false, error: error.message };
        } finally {
            setLoadingAI(false);
        }
        
        setLoadingAI(false);
        
        // Return success result when AI generation is complete
        return { 
            success: true, 
            showPaymentModal: false,
            message: "Layout suggestions generated successfully"
        };
    } catch (error) {
        console.error("Error generating layout suggestions:", error);
        setAIError(`Error: ${error.message}`);
        setLoadingAI(false);
        return { 
            success: false,
            showPaymentModal: false,
            error: error.message
        };
    }
};

// Enhance the function to save layouts to Firestore
export const saveAILayouts = async (db, userId, layouts) => {
    if (!db || !userId || !layouts || !Array.isArray(layouts)) {
        console.error("Missing required parameters for saveAILayouts");
        return false;
    }
    
    try {
        const sessionRef = doc(db, 'sessions', userId);
        
        // Format layouts for proper Firestore storage
        const processedLayouts = layouts.map(layout => {
            // Create a safe copy of the layout object
            const safeLayout = {
                layout_name: layout.title || layout.layout_name || "Untitled Layout",
                description: layout.description || ""
            };
            
            // Process matrix to be Firestore-friendly - Convert nested arrays to maps
            if (layout.matrix && Array.isArray(layout.matrix)) {
                // Convert the matrix from an array of arrays to a map of maps
                // This avoids the "Nested arrays are not supported" Firestore error
                const matrixMap = {};
                
                layout.matrix.forEach((row, rowIndex) => {
                    if (Array.isArray(row)) {
                        const rowMap = {};
                        row.forEach((cell, colIndex) => {
                            rowMap[colIndex.toString()] = cell;
                        });
                        matrixMap[rowIndex.toString()] = rowMap;
                    }
                });
                
                safeLayout.matrix = matrixMap;
                console.log(`Converted matrix for layout "${safeLayout.layout_name}" to Firestore-compatible format`);
            } else {
                // If no matrix, create an empty one
                safeLayout.matrix = {};
            }
            
            return safeLayout;
        });
        
        console.log("Saving AI layouts to Firestore:", processedLayouts);
        
        // Update both fields for compatibility
        await updateDoc(sessionRef, {
            aiGeneratedLayouts: processedLayouts,
            layouts: processedLayouts, // New field for better naming
            layoutsGeneratedAt: new Date().toISOString()
        });
        
        console.log("Successfully saved AI layouts to Firestore");
        return true;
    } catch (error) {
        console.error("Error saving AI layouts:", error);
        return false;
    }
};

// Function to handle payment processing
export const handlePaymentProceed = async ({ currentUser, db, gridSize, setAIError, setIsPaymentModalOpen, priceMapping }) => {
    try {
        // Add enhanced logging for debugging
        console.log("handlePaymentProceed called with:", {
            currentUser: currentUser ? "User exists" : "No user",
            userID: currentUser?.uid,
            gridSize,
            priceMapping: priceMapping ? "Exists" : "Missing"
        });

        if (!currentUser || !currentUser.uid) {
            console.error("User must be logged in to proceed with payment.");
            if (setAIError) setAIError("Please log in to proceed.");
            return false;
        }

        const priceId = priceMapping?.[gridSize];
        if (!priceId) {
            console.error("No price found for this grid size:", gridSize);
            console.log("Available price mappings:", Object.keys(priceMapping || {}));
            if (setAIError) setAIError("Pricing information not available.");
            return false;
        }

        console.log("Creating checkout session with priceId:", priceId);
        
        // Create the checkout session
        const customerRef = doc(db, "customers", currentUser.uid);
        const checkoutSessionRef = collection(customerRef, "checkout_sessions");
        
        const docRef = await addDoc(checkoutSessionRef, {
            price: priceId,
            success_url: window.location.href,
            cancel_url: window.location.href,
            mode: "payment",
            allow_promotion_codes: true,
            metadata: {
                gridSize: gridSize,
                userId: currentUser.uid
            }
        });

        console.log("Checkout session created, waiting for URL...");

        // Set up the listener with proper cleanup
        let unsubscribed = false;
        const unsubscribe = onSnapshot(docRef, (snap) => {
            const data = snap.data();
            console.log("Checkout session updated:", data ? Object.keys(data) : "No data");
            
            const { error, url } = data || {};
            
            if (error) {
                console.error("Checkout session error:", error);
                if (setAIError) setAIError(`An error occurred: ${error.message}`);
                if (setIsPaymentModalOpen) setIsPaymentModalOpen(false);
                unsubscribe();
                return;
            }

            if (!unsubscribed && url) {
                console.log("Redirecting to payment URL:", url);
                window.location.assign(url);
                unsubscribe();
            }
        });

        // Set a timeout that will only trigger if URL redirect hasn't happened
        setTimeout(() => {
            if (!unsubscribed) {
                console.warn("Payment session creation timed out");
                unsubscribed = true;
                unsubscribe();
                if (setIsPaymentModalOpen) setIsPaymentModalOpen(false);
                if (setAIError) setAIError("Payment session creation timed out. Please try again.");
            }
        }, 30000); // 30 seconds timeout

        return true;
    } catch (error) {
        console.error("Error creating checkout session:", error);
        if (setAIError) setAIError(`Payment initialization failed: ${error.message}`);
        if (setIsPaymentModalOpen) setIsPaymentModalOpen(false);
        return false;
    }
};

// Function to get the button text based on the loading and premium status
export const getAIButtonText = (loadingAI, isPremiumUser) => {
    if (loadingAI) return "Generating...";
    if (isPremiumUser) return "Generate 5 AI-Enhanced Layouts";
    return "Generate 5 AI-Enhanced Layouts";
};

// Optional: Create a container component for testing purposes
const AIHandler = ({ children }) => {
    // This component doesn't render anything itself, it's just a utilities container
    return <>{children}</>;
};

export default AIHandler;
